import React, { useState, useEffect, useRef } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Grid,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Divider,
    Avatar,
    List,
    ListItem,
    ListItemText,
    ListItemAvatar,
    Stack,
    Autocomplete,
    InputAdornment,
    useTheme,
    alpha
} from '@mui/material';
import { usePermissions } from '../../../utils/permissions';
import {
    ExpandMore as ExpandMoreIcon,
    Add as AddIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    Warning as WarningIcon,
    Assignment as AssignmentIcon,
    Science as ScienceIcon,
    Speed as SpeedIcon,
    Thermostat as ThermostatIcon,
    Schedule as ScheduleIcon,
    Person as PersonIcon,
    AttachFile as AttachFileIcon,
    CloudUpload as CloudUploadIcon,
    GetApp as GetAppIcon,
    Opacity as WaterDropIcon,
    TrendingUp as TrendingUpIcon,
    Business as BusinessIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { supabase } from '../../../supabaseClient';
import IngredientsLog from './batch-logs-components/IngredientsLog';
import DeviationLog from './batch-logs-components/DeviationLog';
import YieldSummary from './batch-logs-components/YieldSummary';
import SignatureLog from './batch-logs-components/SignatureLog';
import QCChecksLog from './batch-logs-components/QCChecksLog';
import PreMixLog from './batch-logs-components/PreMixLog';

function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`batch-log-tabpanel-${index}`}
            aria-labelledby={`batch-log-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 4, backgroundColor: 'white' }}>{children}</Box>}
        </div>
    );
}

function NavigationFooter({ currentStep, totalSteps, onStepChange, nextStepLabel = "Continue to Next Step", scrollRef, allStepsCompleted = false }) {
    const theme = useTheme();
    const { canAccessPage } = usePermissions();
    const isLastStep = currentStep === totalSteps - 1;
    const isFirstStep = currentStep === 0;
    const showWorkflowComplete = isLastStep && allStepsCompleted;

    // Check if next/previous steps have permissions
    const canAccessNextStep = currentStep < 6 && canAccessPage(getPermissionForStep(currentStep + 1));
    const canAccessPreviousStep = currentStep > 0 && canAccessPage(getPermissionForStep(currentStep - 1));

    function getPermissionForStep(stepId) {
        const permissions = [
            'batch_logs_overview',
            'batch_logs_premix', 
            'batch_logs_ingredients',
            'batch_logs_qc_checks',
            'batch_logs_deviations',
            'batch_logs_yield',
            'batch_logs_signatures'
        ];
        return permissions[stepId] || '';
    }

    const handleNext = () => {
        if (!isLastStep && canAccessNextStep) {
            // Auto-scroll to top before changing step
            if (scrollRef && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            onStepChange(currentStep + 1);
        }
    };

    const handlePrevious = () => {
        if (!isFirstStep && canAccessPreviousStep) {
            // Auto-scroll to top before changing step
            if (scrollRef && scrollRef.current) {
                scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            onStepChange(currentStep - 1);
        }
    };

    return (
        <Box sx={{ 
            mt: 6, 
            pt: 4, 
            borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            background: `linear-gradient(180deg, transparent 0%, ${alpha(theme.palette.grey[50], 0.8)} 100%)`,
            mx: -4,
            px: 4,
            pb: 4
        }}>
            <Box>
                {!isFirstStep && canAccessPreviousStep && (
                    <Button
                        variant="outlined"
                        onClick={handlePrevious}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1.5,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            '&:hover': {
                                borderColor: theme.palette.primary.main,
                                backgroundColor: alpha(theme.palette.primary.main, 0.05)
                            }
                        }}
                    >
                        ← Previous Step
                    </Button>
                )}
            </Box>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Step {currentStep + 1} of {totalSteps}
                </Typography>
                
                {!isLastStep && canAccessNextStep && (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1.5,
                            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            color: 'white',
                            fontWeight: 500,
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                                transform: 'translateY(-1px)',
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                            },
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        {nextStepLabel} →
                    </Button>
                )}
                
                {showWorkflowComplete && (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        backgroundColor: theme.palette.success.main,
                        borderRadius: 3,
                        px: 4,
                        py: 2.5,
                        boxShadow: `0 4px 20px ${alpha(theme.palette.success.main, 0.3)}`,
                        border: `1px solid ${theme.palette.success.dark}`
                    }}>
                        <CheckCircleIcon sx={{ color: 'white', fontSize: 24 }} />
                        <Box>
                            <Typography sx={{
                                color: 'white',
                                fontWeight: 500,
                                fontSize: '1rem',
                                lineHeight: 1
                            }}>
                                Workflow Complete
                            </Typography>
                            <Typography sx={{
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.75rem',
                                fontWeight: 500
                            }}>
                                All steps finished successfully
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
}

function ProcessNavigation({ currentStep, onStepChange, steps, scrollRef, workflowProgress }) {
    const theme = useTheme();
    const [animatingStep, setAnimatingStep] = useState(null);
    
    const handleStepClick = (stepId) => {
        setAnimatingStep(stepId);
        
        // Auto-scroll to top of Mixing Log component
        if (scrollRef && scrollRef.current) {
            scrollRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        setTimeout(() => {
            onStepChange(stepId);
            setAnimatingStep(null);
        }, 200);
    };

    const completedCount = steps.filter(s => s.completed).length;
    const progressPercentage = workflowProgress || (completedCount / steps.length) * 100;

    return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Progress Header */}
            <Box sx={{ mb: 3, textAlign: 'center', flexShrink: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 1 }}>
                    Workflow Progress
                </Typography>
                <Box sx={{ mb: 2 }}>
                    <Typography variant="h3" sx={{ fontWeight: 500, color: theme.palette.success.main, lineHeight: 1 }}>
                        {Math.round(progressPercentage)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {completedCount} of {steps.length} complete
                    </Typography>
                </Box>
                
                {/* Vertical Progress Bar */}
                <Box sx={{ 
                    width: 8, 
                    height: 60,
                    backgroundColor: alpha(theme.palette.grey[300], 0.5), 
                    borderRadius: 4,
                    overflow: 'hidden',
                    position: 'relative',
                    mx: 'auto'
                }}>
                    <Box sx={{
                        width: '100%',
                        height: `${progressPercentage}%`,
                        background: `linear-gradient(180deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                        transition: 'height 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: 4,
                        position: 'absolute',
                        bottom: 0
                    }} />
                </Box>
            </Box>

            {/* Vertical Step List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {steps.map((step, index) => {
                    const isActive = currentStep === step.id;
                    const isCompleted = step.completed;
                    const isAnimating = animatingStep === step.id;
                    
                    return (
                        <Box key={step.id} sx={{ position: 'relative' }}>
                            <Card
                                onClick={() => handleStepClick(step.id)}
                                sx={{
                                    cursor: 'pointer',
                                    borderRadius: 3,
                                    border: `1px solid ${
                                        isCompleted ? theme.palette.success.main : 
                                        isActive ? theme.palette.primary.main : theme.palette.grey[400]
                                    }`,
                                    backgroundColor: isCompleted ? alpha(theme.palette.success.main, 0.1) : 
                                                   isActive ? alpha(theme.palette.primary.main, 0.05) : 'white',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: isAnimating ? 'scale(0.95)' : 
                                              isActive ? 'translateX(4px)' : 'translateX(0px)',
                                    boxShadow: isActive ? `0 4px 16px ${alpha(theme.palette.primary.main, 0.12)}` :
                                               isCompleted ? `0 2px 8px ${alpha(theme.palette.success.main, 0.2)}` :
                                               `0 1px 4px ${alpha(theme.palette.grey[400], 0.08)}`,
                                    '&:hover': {
                                        transform: isAnimating ? 'scale(0.95)' : 'translateX(6px)',
                                        boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.15)}`,
                                        borderColor: isCompleted ? theme.palette.success.main : theme.palette.primary.main
                                    },
                                    '&:active': {
                                        transform: 'scale(0.98) translateX(2px)'
                                    }
                                }}
                            >
                                {/* Active Indicator */}
                                {isActive && !isCompleted && (
                                    <Box sx={{
                                        position: 'absolute',
                                        top: -2,
                                        bottom: -2,
                                        left: -2,
                                        width: 4,
                                        background: `linear-gradient(180deg, ${theme.palette.warning.main}, ${theme.palette.warning.light})`,
                                        borderRadius: '4px 0 0 4px'
                                    }} />
                                )}

                                <CardContent sx={{ 
                                    p: 2, 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 2.5
                                }}>
                                    {/* Icon */}
                                    <Box sx={{ 
                                        fontSize: 24, 
                                        color: isCompleted ? theme.palette.success.main : 
                                               isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                        transition: 'all 0.3s ease',
                                        minWidth: 24
                                    }}>
                                        {isCompleted ? <CheckCircleIcon sx={{ fontSize: 24 }} /> : step.icon}
                                    </Box>

                                    <Box sx={{ flex: 1 }}>
                                        {/* Label */}
                                        <Typography 
                                            variant="body2" 
                                            sx={{ 
                                                fontWeight: 500,
                                                color: isCompleted ? theme.palette.success.main : 
                                                       isActive ? theme.palette.primary.main : theme.palette.text.secondary,
                                                fontSize: '0.875rem',
                                                lineHeight: 1.2,
                                                mb: 0.5
                                            }}
                                        >
                                            {step.label}
                                        </Typography>

                                        {/* Status */}
                                        <Typography 
                                            variant="caption" 
                                            sx={{ 
                                                color: isCompleted ? theme.palette.success.main : 
                                                       isActive ? theme.palette.warning.main : theme.palette.text.disabled,
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.5px'
                                            }}
                                        >
                                            {isCompleted ? '✓ Complete' : 
                                             isActive ? '● Active' : 'Pending'}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>

                            {/* Connecting Line */}
                            {index < steps.length - 1 && (
                                <Box sx={{
                                    position: 'absolute',
                                    bottom: -12,
                                    left: 13,
                                    width: 2,
                                    height: 12,
                                    backgroundColor: isCompleted ? theme.palette.success.main : alpha(theme.palette.grey[400], 0.5),
                                    transition: 'all 0.3s ease'
                                }} />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

export default function BatchLogs({ batchId, batchData, onProductionCompletion, user }) {
    const theme = useTheme();
    const { canAccessPage, canEditPage } = usePermissions();
    const [batchTimeFormat, setBatchTimeFormat] = useState('12hour');
    const [tabValue, setTabValue] = useState(0);
    const [batchLog, setBatchLog] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formulationUnit, setFormulationUnit] = useState('gal');
    const batchLogsRef = useRef(null);
    const [checklist, setChecklist] = useState({
        mixer_sanitized: false,
        containers_cleaned: false,
        gloves_changed: false,
        area_cleared: false,
        ingredients_verified: false,
        operator_name: '',
        operator_initials: '',
        additional_notes: ''
    });
    const [ingredients, setIngredients] = useState([]);
    const [deviations, setDeviations] = useState([]);
    const [signatures, setSignatures] = useState([]);
    const [qcChecks, setQcChecks] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [batchIngredients, setBatchIngredients] = useState([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [qcDocumentationConfirmed, setQcDocumentationConfirmed] = useState(false);
    const [yieldCompleted, setYieldCompleted] = useState(false);

    // Load user profile for time format preference
    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                if (!user?.id) return;
                
                const { data, error } = await supabase
                    .from('profiles')
                    .select('batch_time_format_preference')
                    .eq('id', user.id)
                    .single();
                    
                if (data && !error) {
                    setBatchTimeFormat(data.batch_time_format_preference || '12hour');
                }
            } catch (error) {
                console.error('Error loading user profile:', error);
                setBatchTimeFormat('12hour');
            }
        };
        
        loadUserProfile();
    }, [user?.id]);

    // Set initial tab to first available permission (only on first load)
    useEffect(() => {
        const availableSteps = [
            { id: 0, permission: 'batch_logs_overview' },
            { id: 1, permission: 'batch_logs_premix' },
            { id: 2, permission: 'batch_logs_ingredients' },
            { id: 3, permission: 'batch_logs_qc_checks' },
            { id: 4, permission: 'batch_logs_deviations' },
            { id: 5, permission: 'batch_logs_yield' },
            { id: 6, permission: 'batch_logs_signatures' }
        ].filter(step => canAccessPage(step.permission));

        if (availableSteps.length > 0 && tabValue === 0 && !canAccessPage('batch_logs_overview')) {
            setTabValue(availableSteps[0].id);
        }
    }, [canAccessPage, tabValue]);

    // Load existing batch log data
    useEffect(() => {
        if (batchId) {
            loadBatchLogData();
            loadBatchIngredients();
            checkYieldCompletion();
        }
    }, [batchId]);

    // Load formulation unit
    useEffect(() => {
        const loadFormulationUnit = async () => {
            if (batchData?.formulation_id) {
                try {
                    const { data: formulation, error } = await supabase
                        .from('formulations')
                        .select('unit_of_measure')
                        .eq('id', batchData.formulation_id)
                        .single();
                    
                    if (formulation && !error) {
                        setFormulationUnit(formulation.unit_of_measure || 'gal');
                    }
                } catch (error) {
                    console.error('Error loading formulation unit:', error);
                }
            }
        };
        
        loadFormulationUnit();
    }, [batchData?.formulation_id]);

    // Check yield completion from yield_summary table
    const checkYieldCompletion = async () => {
        try {
            if (!batchId) return;

            const { data, error } = await supabase
                .from('yield_summary')
                .select('theoretical_yield_kg, actual_yield_kg, waste_amount_kg, final_ph, final_viscosity, final_color, final_texture, end_time, recoverable_waste_kg, unrecoverable_waste_kg')
                .eq('batch_id', batchId)
                .single();

            if (data && !error) {
                const hasValue = (value) => value !== '' && value !== null && value !== undefined;
                
                const isComplete = !!(
                    hasValue(data.theoretical_yield_kg) && 
                    hasValue(data.actual_yield_kg) && 
                    hasValue(data.waste_amount_kg) &&
                    hasValue(data.final_ph) && 
                    hasValue(data.final_viscosity) &&
                    hasValue(data.final_color) &&
                    hasValue(data.final_texture) &&
                    hasValue(data.end_time) &&
                    hasValue(data.recoverable_waste_kg) &&
                    hasValue(data.unrecoverable_waste_kg)
                );
                setYieldCompleted(isComplete);
            } else {
                setYieldCompleted(false);
            }
        } catch (error) {
            setYieldCompleted(false);
        }
    };

    // Dynamic workflow progress tracking
    const [workflowProgress, setWorkflowProgress] = useState(0);
    const [completionStatus, setCompletionStatus] = useState({});
    
    useEffect(() => {
        const requiredSignatures = signatures.filter(sig => 
            ['Production Supervisor', 'Quality Control', 'Final Release'].includes(sig.role)
        );
        
        const stepChecks = {
            overview: !!(batchLog?.operator_name && batchLog?.production_location && batchLog?.mixer_equipment),
            premix: !!(checklist?.operator_signature && checklist?.mixer_sanitized && checklist?.raw_materials_inspected && checklist?.equipment_calibrated && checklist?.environmental_conditions_verified && checklist?.ppe_verified && checklist?.batch_record_prepared && checklist?.quality_standards_reviewed && checklist?.work_area_cleared),
            ingredients: batchIngredients.length > 0 && 
                        batchIngredients.every(batchIng => 
                            ingredients.some(loggedIng => 
                                loggedIng.batch_ingredient_id === batchIng.id && 
                                loggedIng.actual_quantity && 
                                loggedIng.time_of_addition
                            )
                        ),
            qcChecks: qcDocumentationConfirmed || (qcChecks.length > 0 && qcChecks.every(qc => qc.pass_fail_status === 'PASS' && qc.tested_at)),
            deviations: (!deviations || deviations.length === 0) || deviations.filter(d => 
                d.status === 'open' || 
                d.status === 'capa_required' || 
                d.status === 'investigating'
            ).length === 0,
            yield: yieldCompleted,
            signatures: requiredSignatures.length >= 3 && requiredSignatures.every(sig => sig.signer_name && sig.signed_at)
        };
        
        // Calculate workflow progress percentage
        const completedSteps = Object.values(stepChecks).filter(step => step === true).length;
        const totalSteps = Object.keys(stepChecks).length;
        const progressPercentage = Math.round((completedSteps / totalSteps) * 100);
        
        setWorkflowProgress(progressPercentage);
        setCompletionStatus(stepChecks);
        
        // Update database when workflow hits 100% or drops below 100%
        const isNowComplete = progressPercentage === 100;
        const currentDbState = batchLog?.batch_log_completed || false;
        
        if (batchLog?.id && !loading && isNowComplete !== currentDbState) {
            updateBatchLogCompletion(isNowComplete);
        }
        
    }, [batchLog, checklist, ingredients, qcChecks, deviations, signatures, batchIngredients, qcDocumentationConfirmed, yieldCompleted, loading]);

    // Notify parent component with workflow progress
    useEffect(() => {
        if (onProductionCompletion && Object.keys(completionStatus).length > 0) {
            const isComplete = workflowProgress === 100;
            onProductionCompletion(isComplete, completionStatus);
        }
    }, [workflowProgress, completionStatus, onProductionCompletion]);

    // Force refresh data when switching tabs to ensure completion status is current
    const handleTabChange = (newTabValue) => {
        setTabValue(newTabValue);
        
        // Auto-scroll to top of Mixing Log component
        if (batchLogsRef.current) {
            batchLogsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        
        // Trigger a refresh of related data to ensure completion status is accurate
        if (batchLog?.id) {
            setRefreshTrigger(prev => prev + 1);
            loadRelatedData(batchLog.id, batchId);
        }
    };

    // Refresh data when refreshTrigger changes
    useEffect(() => {
        if (refreshTrigger > 0 && batchLog?.id) {
            loadRelatedData(batchLog.id, batchId);
        }
    }, [refreshTrigger, batchLog?.id]);

    // Update batch log completion status in database
    const updateBatchLogCompletion = async (isCompleted) => {
        if (!batchLog?.id) return;
        
        try {
            const updateData = {
                batch_log_completed: isCompleted,
                batch_log_completed_at: isCompleted ? new Date().toISOString() : null
            };
            
            const { error } = await supabase
                .from('batch_logs')
                .update(updateData)
                .eq('id', batchLog.id);
                
            if (error) {
                console.error('Error updating batch log completion:', error);
            }
        } catch (error) {
            console.error('Error updating batch log completion:', error);
        }
    };

    // Callback for child components to trigger refresh
    const handleDataUpdate = () => {
        setRefreshTrigger(prev => prev + 1);
        if (batchLog?.id) {
            loadRelatedData(batchLog.id, batchId);
        }
        // Check yield completion when data updates
        checkYieldCompletion();
    };

    // Load batch ingredients from batch_ingredients table
    const loadBatchIngredients = async () => {
        try {
            const { data, error } = await supabase
                .from('batch_ingredients')
                .select(`
                    *,
                    raw_materials:raw_material_id (
                        id, name, cas_number, sku, category, 
                        material_type, primary_unit_measure, color, form,
                        density_g_ml, primary_unit_type, primary_unit_quantity,
                        supplier:supplier_id (
                            id, name
                        )
                    ),
                    formulation_phases:formulation_phase_id (
                        id, name, sequence
                    )
                `)
                .eq('batch_id', batchId)
                .order('sequence_number');

            if (error) {
                console.error('Error loading batch ingredients:', error);
                return;
            }
            
            setBatchIngredients(data || []);
        } catch (error) {
            console.error('Error loading batch ingredients:', error);
        }
    };

    const loadBatchLogData = async () => {
        setLoading(true);
        try {
            const { data: logData, error: logError } = await supabase
                .from('batch_logs')
                .select('*')
                .eq('batch_id', batchId)
                .single();

            if (logError && logError.code !== 'PGRST116') {
                console.error('Error loading batch log:', logError);
                if (logError.message.includes('406')) {
                    console.error('Table may not exist or RLS policy blocking access');
                }
                return;
            }

            if (logData) {
                setBatchLog(logData);
                setQcDocumentationConfirmed(logData.qc_documentation_confirmed || false);
                await loadRelatedData(logData.id, batchId);
            } 
        } catch (error) {
            console.error('Error loading batch log:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadRelatedData = async (batchLogId, batchId) => {
        try {
            const [
                checklistRes,
                ingredientsRes,
                deviationsRes,
                signaturesRes,
                qcChecksRes,
                attachmentsRes,
                batchLogRes
            ] = await Promise.all([
                supabase.from('batch_log_checklists').select('*').eq('batch_log_id', batchLogId),
                supabase.from('batch_log_ingredients').select('*').eq('batch_log_id', batchLogId).order('addition_order'),
                supabase.from('batch_log_deviations').select('*').eq('batch_log_id', batchLogId).order('occurred_at'),
                supabase.from('batch_signatures').select('*').eq('batch_id', batchId).order('signed_at'),
                supabase.from('batch_log_qc_checks').select('*').eq('batch_log_id', batchLogId).order('tested_at'),
                supabase.from('batch_log_attachments').select('*').eq('batch_log_id', batchLogId).order('created_at'),
                supabase.from('batch_logs').select('qc_documentation_confirmed').eq('id', batchLogId).single()
            ]);

            // Log any errors but continue loading other data
            if (checklistRes.error) console.error('Error loading checklist:', checklistRes.error);
            if (ingredientsRes.error) console.error('Error loading ingredients:', ingredientsRes.error);
            if (deviationsRes.error) console.error('Error loading deviations:', deviationsRes.error);
            if (signaturesRes.error) console.error('Error loading signatures:', signaturesRes.error);
            if (qcChecksRes.error) console.error('Error loading QC checks:', qcChecksRes.error);
            if (attachmentsRes.error) console.error('Error loading attachments:', attachmentsRes.error);
            if (batchLogRes.error) console.error('Error loading batch log QC status:', batchLogRes.error);

            if (checklistRes.data && checklistRes.data.length > 0) {
                setChecklist(checklistRes.data[0]);
            }
            if (ingredientsRes.data) setIngredients(ingredientsRes.data);
            if (deviationsRes.data) setDeviations(deviationsRes.data);
            if (signaturesRes.data) setSignatures(signaturesRes.data);
            if (qcChecksRes.data) setQcChecks(qcChecksRes.data);
            if (attachmentsRes.data) setAttachments(attachmentsRes.data);
            if (batchLogRes.data) setQcDocumentationConfirmed(batchLogRes.data.qc_documentation_confirmed || false);
            
        } catch (error) {
            console.error('Error loading related data:', error);
        }
    };



    const createBatchLog = async () => {
        if (!batchLog) {
            try {
                setError(null); // Clear previous errors
                
                const { data: profile, error: profileError } = await supabase.auth.getUser();
                if (profileError) {
                    const errorMsg = 'Authentication error. Please log in again.';
                    console.error('Auth error:', profileError);
                    setError(errorMsg);
                    return null;
                }

                if (!profile?.user?.id) {
                    const errorMsg = 'No user session found. Please log in.';
                    console.error('No user found');
                    setError(errorMsg);
                    return null;
                }

                // Try to get company by ownership first, then by membership
                let { data: userCompany, error: companyError } = await supabase
                    .from('companies')
                    .select('id')
                    .eq('created_by', profile.user.id)
                    .single();

                // If user is not company owner, check if they're a company user
                if (companyError && companyError.code === 'PGRST116') {
                    const { data: companyUser, error: memberError } = await supabase
                        .from('company_users')
                        .select('company_id, companies!inner(id)')
                        .eq('user_id', profile.user.id)
                        .eq('status', 'active')
                        .single();

                    if (memberError) {
                        companyError = memberError;
                    } else if (companyUser) {
                        userCompany = { id: companyUser.company_id };
                        companyError = null;
                    }
                }

                if (companyError) {
                    const errorMsg = companyError.code === 'PGRST116' 
                        ? 'No company found for your account. Please create a company first.' 
                        : `Database error: ${companyError.message}`;
                    console.error('Company error:', companyError);
                    setError(errorMsg);
                    return null;
                }

                if (!userCompany?.id) {
                    const errorMsg = 'No company found for your account. Please create a company first.';
                    console.error('No company found for user');
                    setError(errorMsg);
                    return null;
                }

                const { data: newLog, error } = await supabase
                    .from('batch_logs')
                    .insert({
                        batch_id: batchId,
                        company_id: userCompany.id,
                        operator_name: 'TBD',
                        start_time: new Date().toISOString()
                    })
                    .select()
                    .single();

                if (error) {
                    const errorMsg = error.code === '42P01' 
                        ? 'Batch logs table does not exist. Please run the SQL script to create tables.' 
                        : `Database error: ${error.message}`;
                    console.error('Insert error:', error);
                    setError(errorMsg);
                    return null;
                }
                
                setBatchLog(newLog);
                setError(null); // Clear error on success
                return newLog.id;
            } catch (error) {
                const errorMsg = 'Unexpected error creating batch log. Check console for details.';
                console.error('Error creating batch log:', error);
                setError(errorMsg);
                return null;
            }
        }
        return batchLog.id;
    };

    // Check if user has permission to view ANY batch log tabs
    const hasAnyBatchLogPermission = 
        canAccessPage('batch_logs_overview') ||
        canAccessPage('batch_logs_premix') ||
        canAccessPage('batch_logs_ingredients') ||
        canAccessPage('batch_logs_qc_checks') ||
        canAccessPage('batch_logs_deviations') ||
        canAccessPage('batch_logs_yield') ||
        canAccessPage('batch_logs_signatures');

    // Permission check for any batch logs access
    if (!hasAnyBatchLogPermission) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                px: 3
            }}>
                <Card sx={{
                    p: 8,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 600,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 6,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2.5rem',
                        letterSpacing: '-0.8px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 4,
                        fontWeight: 500,
                        fontSize: '1.2rem'
                    }}>
                        Batch Logs View Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        maxWidth: 450,
                        mx: 'auto'
                    }}>
                        You don't currently have the necessary permissions to access any Batch Logs sections. 
                        Contact your system administrator to request access or verify your role permissions.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Box ref={batchLogsRef} sx={{ 
                p: 2,
                pl: 3,
                pr: 3,
                maxWidth: '98vw',
                width: '100%'
            }}>
                {/* Header with Export Button */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ 
                            p: 1.5, 
                            borderRadius: 2, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            backdropFilter: 'blur(10px)'
                        }}>
                            <AssignmentIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                        </Box>
                        <Box>
                            <Typography variant="h4" sx={{ fontWeight: 500, mb: 0.5, color: theme.palette.text.primary }}>
                                Mixing Log
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                Batch documentation
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Error Alert */}
                {error && (
                    <Alert 
                        severity="error" 
                        onClose={() => setError(null)}
                        sx={{ 
                            mb: 4,
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.error.main}`,
                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                            fontWeight: 500
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                            {error}
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ 
                    display: 'flex', 
                    gap: 0, 
                    borderRadius: 4, 
                    backgroundColor: 'white', 
                    border: `1px solid ${theme.palette.grey[400]}`, 
                    overflow: 'hidden',
                    backdropFilter: 'blur(20px)',
                    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                    '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}>
                    {/* Sidebar Navigation */}
                    <Box sx={{ 
                        width: 280, 
                        bgcolor: `linear-gradient(180deg, ${alpha(theme.palette.grey[50], 0.8)}, ${alpha(theme.palette.grey[100], 0.9)})`, 
                        borderRight: `1px solid ${theme.palette.grey[400]}`,
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '80vh',
                        backdropFilter: 'blur(10px)'
                    }}>
                        <ProcessNavigation 
                            currentStep={tabValue}
                            onStepChange={handleTabChange}
                            scrollRef={batchLogsRef}
                            workflowProgress={workflowProgress}
                            steps={[
                                { 
                                    id: 0, 
                                    label: 'Overview', 
                                    icon: <AssignmentIcon />,
                                    completed: completionStatus.overview || false,
                                    hasPermission: canAccessPage('batch_logs_overview')
                                },
                                { 
                                    id: 1, 
                                    label: 'Pre-Mix', 
                                    icon: <CheckCircleIcon />,
                                    completed: completionStatus.premix || false,
                                    hasPermission: canAccessPage('batch_logs_premix')
                                },
                                { 
                                    id: 2, 
                                    label: 'Ingredients', 
                                    icon: <ScienceIcon />,
                                    completed: completionStatus.ingredients || false,
                                    hasPermission: canAccessPage('batch_logs_ingredients')
                                },
                                { 
                                    id: 3, 
                                    label: 'QC Checks', 
                                    icon: <SpeedIcon />,
                                    completed: completionStatus.qcChecks || false,
                                    hasPermission: canAccessPage('batch_logs_qc_checks')
                                },
                                { 
                                    id: 4, 
                                    label: 'Deviations', 
                                    icon: <WarningIcon />,
                                    completed: completionStatus.deviations || false,
                                    hasPermission: canAccessPage('batch_logs_deviations')
                                },
                                { 
                                    id: 5, 
                                    label: 'Yield', 
                                    icon: <TrendingUpIcon />,
                                    completed: completionStatus.yield || false,
                                    hasPermission: canAccessPage('batch_logs_yield')
                                },
                                { 
                                    id: 6, 
                                    label: 'Signatures', 
                                    icon: <PersonIcon />,
                                    completed: completionStatus.signatures || false,
                                    hasPermission: canAccessPage('batch_logs_signatures')
                                }
                            ].filter(step => step.hasPermission)}
                        />
                    </Box>

                    {/* Main Content Area */}
                    <Box sx={{ flex: 1, minHeight: '80vh' }}>
                        <TabPanel value={tabValue} index={0}>
                            {canAccessPage('batch_logs_overview') ? (
                                <>
                                    <MixingOverview
                                        batchLog={batchLog}
                                        setBatchLog={setBatchLog}
                                        batchId={batchId}
                                        batchData={batchData}
                                        createBatchLog={createBatchLog}
                                        onDataUpdate={handleDataUpdate}
                                        canEdit={canEditPage('batch_logs_overview')}
                                        batchTimeFormat={batchTimeFormat}
                                    />
                                    <NavigationFooter 
                                        currentStep={tabValue}
                                        totalSteps={7}
                                        onStepChange={handleTabChange}
                                        nextStepLabel="Start Pre-Mix Checklist"
                                        scrollRef={batchLogsRef}
                                    />
                                </>
                            ) : (
                                <Box sx={{ p: 4 }}>No content available</Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            {canAccessPage('batch_logs_premix') ? (
                                <>
                                    <PreMixLog 
                                        checklist={checklist}
                                        setChecklist={setChecklist}
                                        batchLogId={batchLog?.id}
                                        createBatchLog={createBatchLog}
                                        onDataUpdate={handleDataUpdate}
                                        batchTimeFormat={batchTimeFormat}
                                    />
                                    <NavigationFooter 
                                        currentStep={tabValue}
                                        totalSteps={7}
                                        onStepChange={handleTabChange}
                                        nextStepLabel="Begin Ingredient Mixing"
                                        scrollRef={batchLogsRef}
                                    />
                                </>
                            ) : (
                                <Box sx={{ p: 4 }}>No content available</Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={2}>
                            {canAccessPage('batch_logs_ingredients') ? (
                                <>
                                    <IngredientsLog 
                                        ingredients={ingredients}
                                        setIngredients={setIngredients}
                                        batchLogId={batchLog?.id}
                                        batchData={batchData}
                                        createBatchLog={createBatchLog}
                                        batchIngredients={batchIngredients}
                                        batchLog={batchLog}
                                        batchNumber={batchData?.batch_number}
                                        lotNumber={batchData?.lot_number}
                                        showSnackbar={(message, severity) => {
                                        }}
                                        onDataUpdate={handleDataUpdate}
                                        batchTimeFormat={batchTimeFormat}
                                    />
                                    <NavigationFooter 
                                        currentStep={tabValue}
                                        totalSteps={7}
                                        onStepChange={handleTabChange}
                                        nextStepLabel="Start Quality Checks"
                                        scrollRef={batchLogsRef}
                                    />
                                </>
                            ) : (
                                <Box sx={{ p: 4 }}>No content available</Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={3}>
                            {canAccessPage('batch_logs_qc_checks') ? (
                                <>
                                    <QCChecksLog 
                                        batchId={batchId}
                                        batchLogId={batchLog?.id}
                                        productInfo={batchData?.product}
                                        companyId={batchData?.company_id}
                                        batchNumber={batchData?.batch_number}
                                        batchIngredients={batchIngredients}
                                        showSnackbar={(message, severity) => {}}
                                        onDataUpdate={handleDataUpdate}
                                        batchTimeFormat={batchTimeFormat}
                                    />
                                    <NavigationFooter 
                                        currentStep={tabValue}
                                        totalSteps={7}
                                        onStepChange={handleTabChange}
                                        nextStepLabel="Review Deviations"
                                        scrollRef={batchLogsRef}
                                    />
                                </>
                            ) : (
                                <Box sx={{ p: 4 }}>No content available</Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={4}>
                            {canAccessPage('batch_logs_deviations') ? (
                                <>
                                    <DeviationLog 
                                        deviations={deviations}
                                        setDeviations={setDeviations}
                                        batchLogId={batchLog?.id}
                                        createBatchLog={createBatchLog}
                                        showSnackbar={(message, severity) => {
                                        }}
                                        onDataUpdate={handleDataUpdate}
                                        batchTimeFormat={batchTimeFormat}
                                    />
                                    <NavigationFooter 
                                        currentStep={tabValue}
                                        totalSteps={7}
                                        onStepChange={handleTabChange}
                                        nextStepLabel="Calculate Yield"
                                        scrollRef={batchLogsRef}
                                    />
                                </>
                            ) : (
                                <Box sx={{ p: 4 }}>No content available</Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={5}>
                            {canAccessPage('batch_logs_yield') ? (
                                <>
                                    <YieldSummary 
                                        formulationUnit={formulationUnit}
                                        onDataUpdate={handleDataUpdate}
                                        batchId={batchId}
                                        batchTimeFormat={batchTimeFormat}
                                    />
                                    <NavigationFooter 
                                        currentStep={tabValue}
                                        totalSteps={7}
                                        onStepChange={handleTabChange}
                                        nextStepLabel="Final Signatures"
                                        scrollRef={batchLogsRef}
                                    />
                                </>
                            ) : (
                                <Box sx={{ p: 4 }}>No content available</Box>
                            )}
                        </TabPanel>

                        <TabPanel value={tabValue} index={6}>
                            {canAccessPage('batch_logs_signatures') ? (
                                <>
                                    <SignatureLog 
                                        signatures={signatures}
                                        setSignatures={setSignatures}
                                        batchId={batchId}
                                        onDataUpdate={handleDataUpdate}
                                        batchTimeFormat={batchTimeFormat}
                                    />
                                    <NavigationFooter 
                                        currentStep={tabValue}
                                        totalSteps={7}
                                        onStepChange={handleTabChange}
                                        nextStepLabel="Complete Batch"
                                        scrollRef={batchLogsRef}
                                        allStepsCompleted={Object.values(completionStatus).every(status => status === true)}
                                    />
                                </>
                            ) : (
                                <Box sx={{ p: 4 }}>No content available</Box>
                            )}
                        </TabPanel>
                    </Box>
                </Box>
            </Box>
        </LocalizationProvider>
    );
}

// Mixing Overview Component
function MixingOverview({ batchLog, setBatchLog, batchId, batchData, createBatchLog, canEdit = false, batchTimeFormat = '12hour' }) {
    const theme = useTheme();
    const { canAccessPage } = usePermissions();

    const getBatchPlannedStart = (bd) => {
        if (!bd?.planned_start_date) return null;
        const str = bd.planned_start_time
            ? `${bd.planned_start_date} ${bd.planned_start_time}`
            : bd.planned_start_date;
        return dayjs(str).isValid() ? dayjs(str) : null;
    };

    const [formData, setFormData] = useState({
        operator_name: '',
        operator_signature: '',
        production_location: '',
        production_room: '',
        mixer_equipment: '',
        mixer_model: '',
        equipment_id: '',
        start_time: getBatchPlannedStart(batchData),
        temperature_value: '',
        temperature_unit: 'F',
        cleaning_status_confirmed: false,
        cleaning_signature: '',
        cleaning_confirmed_at: '',
        general_notes: ''
    });

    const [cleaningSignatureDialog, setCleaningSignatureDialog] = useState(false);
    const [cleaningPreviewName, setCleaningPreviewName] = useState('');
    const [errors, setErrors] = useState({});
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [locations, setLocations] = useState([]);
    const [loadingLocations, setLoadingLocations] = useState(true);
    const [locationSelectionDialogOpen, setLocationSelectionDialogOpen] = useState(false);
    const [locationSearchTerm, setLocationSearchTerm] = useState('');

    useEffect(() => {
        if (batchLog) {
            setFormData({
                operator_name: batchLog.operator_name || '',
                operator_signature: batchLog.operator_signature || '',
                production_location: batchLog.production_location || '',
                production_room: batchLog.production_room || '',
                mixer_equipment: batchLog.mixer_equipment || '',
                mixer_model: batchLog.mixer_model || '',
                equipment_id: batchLog.equipment_id || '',
                start_time: batchLog.start_time ? dayjs(batchLog.start_time) : getBatchPlannedStart(batchData),
                temperature_value: batchLog.temperature_value || batchLog.temperature_celsius || '',
                temperature_unit: batchLog.temperature_unit || 'F',
                cleaning_status_confirmed: batchLog.cleaning_status_confirmed || false,
                cleaning_signature: batchLog.cleaning_signature || '',
                cleaning_confirmed_at: batchLog.cleaning_confirmed_at || '',
                general_notes: batchLog.general_notes || ''
            });
        }
    }, [batchLog]);

    useEffect(() => {
        loadLocations();
    }, []);

    const loadLocations = async () => {
        try {
            setLoadingLocations(true);
            
            const { data, error } = await supabase
                .from('locations')
                .select('id, name, city, state, facility_type, is_main')
                .eq('is_active', true)
                .order('is_main', { ascending: false })
                .order('name');

            if (error) {
                console.error('Error loading locations:', error);
                return;
            }

            // Sort locations to ensure main location appears first
            const locationsList = (data || []).sort((a, b) => {
                if (a.is_main && !b.is_main) return -1;
                if (!a.is_main && b.is_main) return 1;
                return (a.name || '').localeCompare(b.name || '');
            });
            
            setLocations(locationsList);
            
            // Resolve production_location: if it's a UUID look it up by id, if it's a name validate it
            const currentLoc = formData.production_location || batchData?.production_location || '';
            if (currentLoc) {
                const isUuid = /^[0-9a-f-]{36}$/i.test(currentLoc);
                if (isUuid) {
                    const match = locationsList.find(loc => loc.id === currentLoc);
                    setFormData(prev => ({ ...prev, production_location: match?.name || '' }));
                } else {
                    const isValid = locationsList.some(loc => loc.name === currentLoc);
                    if (!isValid) setFormData(prev => ({ ...prev, production_location: '' }));
                }
            }
        } catch (error) {
            console.error('Error loading locations:', error);
        } finally {
            setLoadingLocations(false);
        }
    };

    // Location dialog handlers
    const handleOpenLocationSelection = () => {
        setLocationSearchTerm('');
        setLocationSelectionDialogOpen(true);
    };

    const handleCloseLocationSelection = () => {
        setLocationSelectionDialogOpen(false);
        setLocationSearchTerm('');
    };

    const handleSelectLocation = async (location) => {
        setFormData({ ...formData, production_location: location.name });
        handleCloseLocationSelection();
    };

    const getFilteredLocations = () => {
        const searchTerm = locationSearchTerm.toLowerCase();
        const filtered = !searchTerm.trim() 
            ? locations 
            : locations.filter(location => 
                location.name?.toLowerCase().includes(searchTerm) ||
                location.facility_type?.toLowerCase().includes(searchTerm) ||
                location.city?.toLowerCase().includes(searchTerm) ||
                location.state?.toLowerCase().includes(searchTerm)
            );
        
        // Maintain sort order: main location first, then alphabetical
        return filtered.sort((a, b) => {
            if (a.is_main && !b.is_main) return -1;
            if (!a.is_main && b.is_main) return 1;
            return (a.name || '').localeCompare(b.name || '');
        });
    };

    useEffect(() => {
        // Initialize preview with current cleaning signature when dialog opens
        if (cleaningSignatureDialog) {
            setCleaningPreviewName(formData.cleaning_signature || '');
        }
    }, [cleaningSignatureDialog, formData.cleaning_signature]);

    const validateForm = () => {
        const newErrors = {};
        
        if (!formData.operator_name.trim()) {
            newErrors.operator_name = 'Operator name is required';
        }
        
        if (!formData.production_location.trim()) {
            newErrors.production_location = 'Production location is required';
        }
        
        if (!formData.mixer_equipment.trim()) {
            newErrors.mixer_equipment = 'Mixer equipment selection is required';
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        try {
            setSaveSuccess(false);
            setErrors({});
            
            if (!validateForm()) {
                return;
            }
            
            const batchLogId = await createBatchLog();
            if (!batchLogId) {
                setErrors({ submit: 'Failed to create or get batch log ID' });
                return;
            }

            const updateData = {
                ...formData,
                start_time: formData.start_time?.toISOString(),
                updated_at: dayjs().toISOString()
            };

            const { data, error } = await supabase
                .from('batch_logs')
                .update(updateData)
                .eq('id', batchLogId)
                .select()
                .single();

            if (error) {
                console.error('Error updating batch log:', error);
                setErrors({ submit: `Database error: ${error.message}` });
                return;
            }
            
            setBatchLog(data);
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        } catch (error) {
            console.error('Error saving mixing overview:', error);
            setErrors({ submit: 'Unexpected error occurred while saving' });
        }
    };

    const handleCleaningSignature = () => {
        const signatureName = document.getElementById('cleaning-signature-name')?.value || '';
        
        if (!signatureName.trim()) {
            return;
        }
        
        setFormData(prev => ({
            ...prev,
            cleaning_signature: signatureName,
            cleaning_status_confirmed: true,
            cleaning_confirmed_at: dayjs().toISOString()
        }));
        
        setCleaningSignatureDialog(false);
    };


    // Permission check for batch logs overview
    if (!canAccessPage('batch_logs_overview')) {
        return (
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                minHeight: '70vh',
                textAlign: 'center',
                px: 3
            }}>
                <Card sx={{
                    p: 8,
                    borderRadius: 3,
                    border: `1px solid ${theme.palette.grey[400]}`,
                    background: 'white',
                    boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                    maxWidth: 600,
                    width: '100%',
                    position: 'relative',
                    overflow: 'hidden',
                    cursor: 'default',
                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                    '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    },
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 4,
                        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                        opacity: 0.8,
                        transition: 'opacity 0.3s ease'
                    },
                    '&:hover::before': {
                        opacity: 1
                    }
                }}>
                    <Box sx={{ 
                        p: 3, 
                        borderRadius: 3, 
                        background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                        mb: 6,
                        display: 'inline-flex',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                    }}>
                        <AssignmentIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                    </Box>
                    <Typography variant="h3" sx={{ 
                        mb: 3, 
                        fontWeight: 500, 
                        color: 'text.primary',
                        fontSize: '2.5rem',
                        letterSpacing: '-0.8px'
                    }}>
                        Access Restricted
                    </Typography>
                    <Typography variant="h6" sx={{ 
                        color: 'text.secondary', 
                        mb: 4,
                        fontWeight: 500,
                        fontSize: '1.2rem'
                    }}>
                        Batch Logs Overview Permissions Required
                    </Typography>
                    <Typography variant="body1" sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        fontSize: '1.05rem',
                        maxWidth: 450,
                        mx: 'auto'
                    }}>
                        You don't currently have the necessary permissions to access the Batch Logs Overview. 
                        Contact your system administrator to request access or verify your role permissions.
                    </Typography>
                </Card>
            </Box>
        );
    }

    return (
        <Box>
            {/* Error Messages */}
            {Object.keys(errors).length > 0 && (
                <Alert 
                    severity="error" 
                    sx={{ 
                        mb: 4,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.error.main}`,
                        backgroundColor: alpha(theme.palette.error.main, 0.1),
                        fontWeight: 500
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 1, color: 'black' }}>
                        Please fix the following errors:
                    </Typography>
                    {Object.entries(errors).map(([field, message]) => (
                        <Typography key={field} variant="body2" sx={{ display: 'block', color: 'black' }}>
                            • {message}
                        </Typography>
                    ))}
                </Alert>
            )}

            {/* Success Message */}
            {saveSuccess && (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mb: 4,
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.success.main}`,
                        backgroundColor: alpha(theme.palette.success.main, 0.1),
                        fontWeight: 500
                    }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        Mixing overview saved successfully!
                    </Typography>
                </Alert>
            )}

            {/* Asymmetrical Dashboard Layout */}
            <Box sx={{ mb: 6 }}>
                <Grid container spacing={3}>
                    {/* Large Hero Card - Operator */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: 'white',
                            height: 220,
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                                zIndex: 10
                            }
                        }}>
                            <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <Box sx={{
                                    width: 45,
                                    height: 45,
                                    borderRadius: '50%',
                                    backgroundColor: theme.palette.primary.main,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mb: 1.5,
                                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`
                                }}>
                                    <PersonIcon sx={{ fontSize: 24, color: 'white' }} />
                                </Box>
                                <Typography variant="h5" sx={{ 
                                    fontWeight: 500, 
                                    color: theme.palette.text.primary, 
                                    mb: 0.5,
                                    textAlign: 'center',
                                    wordBreak: 'break-word'
                                }}>
                                    {formData.operator_name || ''}
                                </Typography>
                                <Typography variant="body2" sx={{ 
                                    fontWeight: 500, 
                                    color: 'text.secondary', 
                                    textTransform: 'uppercase',
                                    letterSpacing: 1
                                }}>
                                    Lead Operator
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Tall Status Stack */}
                    <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: 220 }}>
                            {/* Location Card */}
                            <Card sx={{ 
                                borderRadius: 3,
                                border: formData.production_location ? `1px solid ${theme.palette.success.main}` : `1px solid ${theme.palette.grey[400]}`,
                                backgroundColor: formData.production_location ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                                flex: 1,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                }
                            }}>
                                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 500, 
                                        color: formData.production_location ? theme.palette.success.main : theme.palette.text.disabled, 
                                        mb: 0.5,
                                        textAlign: 'center',
                                        wordBreak: 'break-word'
                                    }}>
                                        {formData.production_location || 'Factory'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ 
                                        fontWeight: 500, 
                                        color: 'text.secondary', 
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5
                                    }}>
                                        Location
                                    </Typography>
                                </CardContent>
                            </Card>

                            {/* Mixer Card */}
                            <Card sx={{ 
                                borderRadius: 3,
                                border: formData.mixer_equipment ? `1px solid ${theme.palette.info.main}` : `1px solid ${theme.palette.grey[400]}`,
                                backgroundColor: formData.mixer_equipment ? alpha(theme.palette.info.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                                flex: 1,
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                                }
                            }}>
                                <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                    <Typography variant="h6" sx={{ 
                                        fontWeight: 500, 
                                        color: formData.mixer_equipment ? theme.palette.info.main : theme.palette.text.disabled, 
                                        mb: 0.5,
                                        textAlign: 'center',
                                        wordBreak: 'break-word'
                                    }}>
                                        {formData.mixer_equipment || 'test'}
                                    </Typography>
                                    <Typography variant="caption" sx={{ 
                                        fontWeight: 500, 
                                        color: 'text.secondary', 
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.5
                                    }}>
                                        Mixer
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Box>
                    </Grid>

                    {/* Cleaning Status Card - Wide */}
                    <Grid item xs={12} md={3}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: formData.cleaning_status_confirmed ? `1px solid ${theme.palette.success.main}` : `1px solid ${theme.palette.error.main}`,
                            backgroundColor: formData.cleaning_status_confirmed ? alpha(theme.palette.success.main, 0.1) : alpha(theme.palette.error.main, 0.1),
                            height: 220,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`
                            }
                        }}>
                            <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                                <CheckCircleIcon sx={{ 
                                    fontSize: 36, 
                                    color: formData.cleaning_status_confirmed ? theme.palette.success.main : theme.palette.error.main, 
                                    mb: 1.5,
                                    filter: `drop-shadow(0 4px 8px ${alpha(theme.palette.primary.main, 0.2)})`
                                }} />
                                <Typography variant="h5" sx={{ 
                                    fontWeight: 500, 
                                    color: formData.cleaning_status_confirmed ? theme.palette.success.main : theme.palette.error.main,
                                    textAlign: 'center',
                                    mb: 0.5
                                }}>
                                    {formData.cleaning_status_confirmed ? 'CLEAN' : 'PENDING'}
                                </Typography>
                                <Typography variant="caption" sx={{ 
                                    fontWeight: 500, 
                                    color: 'text.secondary',
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    textAlign: 'center'
                                }}>
                                    Cleaning Status
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Environmental Cards - Asymmetrical Row */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ 
                            borderRadius: 4,
                            border: formData.temperature_value ? `1px solid ${theme.palette.warning.main}` : `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: formData.temperature_value ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.grey[100], 0.8),
                            height: 170,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`
                            }
                        }}>
                            <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h5" sx={{ 
                                    fontWeight: 500, 
                                    color: formData.temperature_value ? theme.palette.warning.dark : theme.palette.text.disabled,
                                    mb: 0.5
                                }}>
                                    {formData.temperature_value ? `${formData.temperature_value}°${formData.temperature_unit}` : '34°F'}
                                </Typography>
                                <ThermostatIcon sx={{ fontSize: 20, color: formData.temperature_value ? theme.palette.warning.dark : theme.palette.text.disabled, mb: 0.5 }} />
                                <Typography variant="caption" sx={{ 
                                    fontWeight: 500, 
                                    color: 'text.secondary', 
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    textAlign: 'center'
                                }}>
                                    Temperature
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{
                            borderRadius: 4,
                            border: formData.start_time ? `1px solid ${theme.palette.text.primary}` : `1px solid ${theme.palette.grey[400]}`,
                            backgroundColor: formData.start_time ? 'white' : alpha(theme.palette.grey[100], 0.8),
                            height: 170,
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            backdropFilter: 'blur(20px)',
                            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`
                            }
                        }}>
                            <CardContent sx={{ p: 1.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                <Typography variant="h6" sx={{ 
                                    fontWeight: 500, 
                                    color: formData.start_time ? theme.palette.text.primary : theme.palette.text.disabled,
                                    mb: 0.5,
                                    textAlign: 'center'
                                }}>
                                    {formData.start_time ? formData.start_time.format(batchTimeFormat === '24hour' ? 'HH:mm' : 'h:mm A') : '--:--'}
                                </Typography>
                                <ScheduleIcon sx={{ fontSize: 20, color: formData.start_time ? theme.palette.text.primary : theme.palette.text.disabled, mb: 0.5 }} />
                                <Typography variant="caption" sx={{ 
                                    fontWeight: 500, 
                                    color: 'text.secondary', 
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.5,
                                    textAlign: 'center'
                                }}>
                                    Start Time
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                </Grid>
            </Box>

            <Grid container spacing={3}>

                {/* Operator & Location Input Card */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ 
                        minHeight: 400, 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <PersonIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Operator & Location
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Production staff and facility details
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Stack spacing={3} sx={{ flexGrow: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Operator Name *"
                                    value={formData.operator_name}
                                    onChange={(e) => setFormData({...formData, operator_name: e.target.value})}
                                    error={!!errors.operator_name}
                                    helperText={errors.operator_name}
                                    required
                                    disabled={!canEdit}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: errors.operator_name ? theme.palette.error.main : theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: errors.operator_name ? theme.palette.error.main : theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: errors.operator_name ? theme.palette.error.main : theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Production Location *"
                                    value={formData.production_location || ''}
                                    onClick={canEdit ? handleOpenLocationSelection : undefined}
                                    placeholder={canEdit ? "Click to select production location" : "Production location (read-only)"}
                                    error={!!errors.production_location}
                                    helperText={canEdit ? (errors.production_location || 'Click to select from available locations') : 'Read-only - no edit permissions'}
                                    required
                                    disabled={!canEdit}
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <BusinessIcon sx={{ color: theme.palette.grey[600] }} />
                                            </InputAdornment>
                                        )
                                    }}
                                    sx={{
                                        cursor: canEdit ? 'pointer' : 'default',
                                        '& .MuiOutlinedInput-root': {
                                            cursor: canEdit ? 'pointer' : 'default',
                                            '& fieldset': { borderColor: errors.production_location ? theme.palette.error.main : theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: errors.production_location ? theme.palette.error.main : theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: errors.production_location ? theme.palette.error.main : theme.palette.grey[700], borderWidth: 1 }
                                        },
                                        '& .MuiInputBase-input': {
                                            cursor: canEdit ? 'pointer' : 'default'
                                        }
                                    }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Production Room"
                                    value={formData.production_room}
                                    onChange={(e) => setFormData({...formData, production_room: e.target.value})}
                                    disabled={!canEdit}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Equipment Information Card */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ 
                        minHeight: 400, 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2
                                }}>
                                    <SpeedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Equipment Setup
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Mixing equipment and configuration
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Stack spacing={3} sx={{ flexGrow: 1 }}>
                                <TextField
                                    fullWidth
                                    label="Mixer Equipment *"
                                    value={formData.mixer_equipment}
                                    onChange={(e) => setFormData({...formData, mixer_equipment: e.target.value})}
                                    error={!!errors.mixer_equipment}
                                    helperText={errors.mixer_equipment}
                                    required
                                    disabled={!canEdit}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: errors.mixer_equipment ? theme.palette.error.main : theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: errors.mixer_equipment ? theme.palette.error.main : theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: errors.mixer_equipment ? theme.palette.error.main : theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Mixer Model"
                                    value={formData.mixer_model}
                                    onChange={(e) => setFormData({...formData, mixer_model: e.target.value})}
                                    disabled={!canEdit}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                                
                                <TextField
                                    fullWidth
                                    label="Equipment ID"
                                    value={formData.equipment_id}
                                    onChange={(e) => setFormData({...formData, equipment_id: e.target.value})}
                                    disabled={!canEdit}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                            '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                            '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                        }
                                    }}
                                />
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Timing & Environmental Card */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        height: 450,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2,
                                    flexShrink: 0
                                }}>
                                    <ScheduleIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Timing & Environmental Conditions
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Process timing and environmental monitoring
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Stack spacing={3} sx={{ flexGrow: 1 }}>
                                <DateTimePicker
                                    label="Start Time"
                                    value={formData.start_time}
                                    onChange={(newValue) => setFormData({...formData, start_time: newValue})}
                                    format={batchTimeFormat === '24hour' ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY h:mm A'}
                                    ampm={batchTimeFormat !== '24hour'}
                                    disabled={!canEdit}
                                    renderInput={(params) => <TextField {...params} fullWidth 
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />}
                                />
                                
                                
                                <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, alignItems: 'start' }}>
                                    <TextField
                                        label="Temperature"
                                        type="number"
                                        value={formData.temperature_value}
                                        onChange={(e) => setFormData({...formData, temperature_value: e.target.value})}
                                        disabled={!canEdit}
                                        sx={{
                                            width: '100%',
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            }
                                        }}
                                    />
                                    <FormControl sx={{ width: '100%', minWidth: 80 }} disabled={!canEdit}>
                                        <InputLabel sx={{ 
                                            color: theme.palette.text.secondary,
                                            '&.Mui-focused': { color: theme.palette.text.secondary }
                                        }}>Unit</InputLabel>
                                        <Select
                                            value={formData.temperature_unit}
                                            onChange={(e) => setFormData({...formData, temperature_unit: e.target.value})}
                                            label="Unit"
                                            sx={{
                                                '& .MuiOutlinedInput-notchedOutline': { 
                                                    borderColor: theme.palette.grey[400],
                                                    borderWidth: 1
                                                },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { 
                                                    borderColor: theme.palette.grey[600],
                                                    borderWidth: 1
                                                },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                                                    borderColor: theme.palette.grey[700],
                                                    borderWidth: 1
                                                }
                                            }}
                                        >
                                            <MenuItem value="C">°C</MenuItem>
                                            <MenuItem value="F">°F</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                                
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Cleaning Status & Notes Card */}
                <Grid item xs={12} lg={6}>
                    <Card sx={{ 
                        borderRadius: 4, 
                        border: `1px solid ${theme.palette.grey[400]}`,
                        height: 450,
                        background: 'white',
                        backdropFilter: 'blur(20px)',
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                        '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}>
                        <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 3 }}>
                                <Box sx={{
                                    p: 1.5, 
                                    borderRadius: 2, 
                                    background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                    backdropFilter: 'blur(10px)',
                                    mr: 2,
                                    flexShrink: 0
                                }}>
                                    <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                                </Box>
                                <Box>
                                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                        Cleaning Status & Notes
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                        Equipment cleaning verification and general observations
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={formData.cleaning_status_confirmed}
                                                onChange={(e) => setFormData({...formData, cleaning_status_confirmed: e.target.checked})}
                                                disabled={!canEdit}
                                                sx={{ color: theme.palette.grey[600], '&.Mui-checked': { color: theme.palette.primary.main } }}
                                            />
                                        }
                                        label="Cleaning Status Confirmed"
                                        sx={{ fontWeight: 500 }}
                                    />
                                    
                                    <Button 
                                        variant="contained" 
                                        onClick={() => setCleaningSignatureDialog(true)}
                                        disabled={!canEdit || !formData.cleaning_status_confirmed}
                                        sx={{
                                            borderRadius: 2,
                                            px: 3,
                                            py: 1.5,
                                            color: theme.palette.grey[600],
                                            fontWeight: 500,
                                            background: alpha(theme.palette.grey[400], 0.3),
                                            border: `1px solid ${alpha(theme.palette.grey[400], 0.6)}`,
                                            backdropFilter: 'blur(10px)',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.grey[500], 0.4),
                                                borderColor: alpha(theme.palette.grey[500], 0.8)
                                            },
                                            '&:disabled': {
                                                background: alpha(theme.palette.grey[300], 0.5),
                                                color: theme.palette.text.disabled
                                            },
                                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}
                                    >
                                        {formData.cleaning_signature ? 'Change Signature' : 'Add Signature'}
                                    </Button>
                                </Box>

                                {formData.cleaning_signature && (
                                    <Box sx={{ 
                                        border: `1px solid ${theme.palette.primary.main}`, 
                                        p: 2, 
                                        borderRadius: 2, 
                                        bgcolor: alpha(theme.palette.primary.main, 0.05)
                                    }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                            CLEANING SIGNATURE:
                                        </Typography>
                                        <Box sx={{ 
                                            fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                            fontSize: '20px', 
                                            color: theme.palette.text.primary,
                                            textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                            mb: 1
                                        }}>
                                            {formData.cleaning_signature}
                                        </Box>
                                        {formData.cleaning_confirmed_at && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                Signed: {dayjs(formData.cleaning_confirmed_at).format(batchTimeFormat === '24hour' ? 'MMM DD, YYYY HH:mm' : 'MMM DD, YYYY h:mm A')}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                                
                                <Box sx={{ flex: 1, minHeight: 0 }}>
                                    <TextField
                                        fullWidth
                                        multiline
                                        label="General Notes"
                                        value={formData.general_notes}
                                        onChange={(e) => setFormData({...formData, general_notes: e.target.value})}
                                        placeholder={canEdit ? "Enter any additional observations or notes..." : "General notes (read-only)"}
                                        disabled={!canEdit}
                                        sx={{
                                            height: '100%',
                                            '& .MuiOutlinedInput-root': {
                                                height: '100%',
                                                '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                                '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                                '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                            },
                                            '& .MuiOutlinedInput-input': {
                                                height: '100% !important',
                                                overflow: 'auto !important',
                                                resize: 'none'
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button 
                            variant="contained"
                            onClick={handleSave}
                            startIcon={<CheckCircleIcon />}
                            size="large"
                            disabled={!canEdit}
                            sx={{
                                borderRadius: 2,
                                px: 4,
                                py: 1.5,
                                color: canEdit ? theme.palette.text.secondary : theme.palette.text.disabled,
                                fontWeight: 500,
                                background: canEdit ? 'rgba(255, 255, 255, 0.95)' : alpha(theme.palette.grey[300], 0.5),
                                border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                                backdropFilter: 'blur(10px)',
                                '&:hover': {
                                    backgroundColor: canEdit ? alpha(theme.palette.primary.main, 0.04) : alpha(theme.palette.grey[300], 0.5)
                                },
                                '&:disabled': {
                                    background: alpha(theme.palette.grey[300], 0.5),
                                    color: theme.palette.text.disabled
                                },
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                            }}
                        >
                            {canEdit ? 'Save Overview' : 'Read Only - No Edit Permissions'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            {/* Cleaning Signature Dialog */}
            <Dialog 
                open={cleaningSignatureDialog} 
                onClose={() => setCleaningSignatureDialog(false)} 
                maxWidth="sm" 
                fullWidth
                sx={{
                    '& .MuiDialog-paper': {
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[400]}`
                    }
                }}
            >
                <DialogTitle sx={{ 
                    backgroundColor: alpha(theme.palette.grey[100], 0.8), 
                    borderBottom: `1px solid ${theme.palette.grey[400]}`,
                    fontWeight: 500
                }}>
                    Add Cleaning Confirmation Signature
                </DialogTitle>
                <DialogContent sx={{ p: 3 }}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, mt: 2, color: theme.palette.grey[700] }}>
                                Enter your name to confirm cleaning status:
                            </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                            <TextField
                                id="cleaning-signature-name"
                                label="Name"
                                fullWidth
                                autoFocus
                                required
                                defaultValue={formData.cleaning_signature || ''}
                                onChange={(e) => setCleaningPreviewName(e.target.value)}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: theme.palette.grey[400], borderWidth: 1 },
                                        '&:hover fieldset': { borderColor: theme.palette.grey[600], borderWidth: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.palette.grey[700], borderWidth: 1 }
                                    }
                                }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Box sx={{ 
                                mt: 2, 
                                p: 3, 
                                border: `1px solid ${theme.palette.grey[400]}`, 
                                borderRadius: 2, 
                                backgroundColor: alpha(theme.palette.grey[50], 0.8)
                            }}>
                                <Typography variant="body2" gutterBottom sx={{ fontWeight: 500, color: theme.palette.grey[700] }}>
                                    Preview: Your signature will appear in script font
                                </Typography>
                                <Box sx={{ 
                                    fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                    fontSize: '28px', 
                                    color: theme.palette.grey[800],
                                    mt: 2,
                                    mb: 1,
                                    textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                    minHeight: '40px',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}>
                                    {cleaningPreviewName || 'Preview will appear here'}
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                    <Button 
                        onClick={() => setCleaningSignatureDialog(false)}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            color: theme.palette.grey[600],
                            '&:hover': {
                                backgroundColor: alpha(theme.palette.grey[200], 0.5)
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCleaningSignature} 
                        variant="contained"
                        sx={{
                            borderRadius: 2,
                            px: 4,
                            py: 1,
                            background: alpha(theme.palette.grey[600], 0.9),
                            color: 'white',
                            fontWeight: 500,
                            '&:hover': {
                                background: theme.palette.grey[700]
                            }
                        }}
                    >
                        Confirm Signature
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Location Selection Dialog */}
            <Dialog
                open={locationSelectionDialogOpen}
                onClose={handleCloseLocationSelection}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                        maxHeight: '90vh'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    fontWeight: 500,
                    fontSize: '1.2rem',
                    color: 'primary.main',
                    borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: alpha(theme.palette.primary.main, 0.05),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <BusinessIcon sx={{ mr: 2 }} />
                        Select Production Location
                    </Box>
                    <IconButton 
                        onClick={handleCloseLocationSelection} 
                        size="small"
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            '&:hover': {
                                transform: 'scale(1.1)',
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                            }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` }}>
                        <TextField
                            fullWidth
                            placeholder="Search locations by name, facility type, city, or state..."
                            value={locationSearchTerm}
                            onChange={(e) => setLocationSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <BusinessIcon color="action" />
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 3,
                                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                    background: 'white',
                                    fontSize: '0.9rem',
                                    '& fieldset': { border: 'none' },
                                    '&:hover': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                                    },
                                    '&.Mui-focused': {
                                        border: `1px solid ${theme.palette.primary.main}`,
                                        boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                    }
                                }
                            }}
                        />
                    </Box>
                    <TableContainer sx={{ maxHeight: 400 }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Location
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`
                                    }}>
                                        Details
                                    </TableCell>
                                    <TableCell sx={{ 
                                        fontWeight: 500, 
                                        fontSize: '0.9rem',
                                        background: alpha(theme.palette.primary.main, 0.05),
                                        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                                        width: 100
                                    }}>
                                        Action
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {getFilteredLocations().length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} sx={{ textAlign: 'center', py: 4 }}>
                                            <Typography color="text.secondary">
                                                {locationSearchTerm ? 'No locations found matching your search.' : 'No production locations available.'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    getFilteredLocations().map((location) => (
                                        <TableRow
                                            key={location.id}
                                            hover
                                            sx={{
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.primary.main, 0.05)
                                                }
                                            }}
                                            onClick={() => handleSelectLocation(location)}
                                        >
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                                                        {location.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {location.facility_type}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                                    {location.city}, {location.state}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleSelectLocation(location);
                                                    }}
                                                    sx={{
                                                        borderRadius: 2,
                                                        fontWeight: 500,
                                                        fontSize: '0.8rem',
                                                        px: 2,
                                                        py: 0.5,
                                                        '&:hover': {
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                                                        }
                                                    }}
                                                >
                                                    Select
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, justifyContent: 'space-between' }}>
                    <Button 
                        onClick={() => {
                            const companyId = batchLog?.company_id;
                            window.open(`/company/${companyId}/locations`, '_blank');
                        }}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.primary.main}`,
                            color: theme.palette.primary.main,
                            fontWeight: 500,
                            px: 3,
                            py: 1
                        }}
                    >
                        Add Location
                    </Button>
                    <Button 
                        onClick={handleCloseLocationSelection}
                        sx={{
                            borderRadius: 2,
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            fontWeight: 500,
                            px: 3,
                            py: 1,
                            '&:hover': {
                                transform: 'translateY(-1px)',
                                border: `1px solid ${theme.palette.primary.main}`,
                                boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.15)}`
                            }
                        }}
                    >
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}